import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import { useUpdateProfile } from "@/api/hooks";
import type { UpdateProfileRequest } from "@/api/models/api-types";
import { useToast } from "@/features/toast";
import { useTryOnPhotos } from "@/features/settings/profile/hooks/use-try-on-photos";
import useAuthStore from "@/store/use-auth-store";
import { usePersonaSetupStore } from "../store/persona-setup-store";
import { PERSONA_QUESTS, type PersonaQuestId } from "../lib/completeness";
import { usePersonaCompleteness } from "./use-persona-completeness";
import { ONBOARDING_PATH } from "./use-post-login-gate";

export const trackPersonaSetup = (
  event: string,
  props?: Record<string, unknown>
) => {
  try {
    if (typeof mixpanel.track === "function") {
      mixpanel.track(event, props);
    }
  } catch {
    // ignore
  }
};

/**
 * Quest adımlarının facade'i. Yan etkiler (viewed / %100 yönlendirme)
 * PersonaSetupPage'de durur — her adım bu hook'u çağırabilir.
 */
export const usePersonaSetup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error: toastError } = useToast();
  const updateProfile = useUpdateProfile();
  const tryOn = useTryOnPhotos();
  const completeness = usePersonaCompleteness();
  const shouldShowOnboarding = useAuthStore(
    (state) => state.user?.gate?.needsOnboarding === true
  );
  const activeQuest = usePersonaSetupStore((state) => state.activeQuest);
  const setActiveQuest = usePersonaSetupStore((state) => state.setActiveQuest);

  const leave = useCallback(() => {
    const needsOnboarding =
      useAuthStore.getState().user?.gate?.needsOnboarding === true;
    navigate(needsOnboarding ? ONBOARDING_PATH : "/", { replace: true });
  }, [navigate]);

  const skip = useCallback(() => {
    trackPersonaSetup("persona_setup_skipped", {
      completed: completeness.completedCount,
      active: activeQuest,
    });
    const userId = useAuthStore.getState().user?._id;
    if (userId) usePersonaSetupStore.getState().dismiss(userId);
    leave();
  }, [activeQuest, completeness.completedCount, leave]);

  /** Kaydı dener; başarısızsa toast gösterir ve false döner (adım ilerlemez). */
  const saveFields = useCallback(
    async (fields: UpdateProfileRequest): Promise<boolean> => {
      try {
        await updateProfile.mutateAsync(fields);
        return true;
      } catch (saveError) {
        console.error("Persona kaydı başarısız:", saveError);
        toastError(t("personaSetup.saveError"));
        return false;
      }
    },
    [updateProfile, toastError, t]
  );

  const completeQuest = useCallback(
    (quest: PersonaQuestId) => {
      trackPersonaSetup("persona_setup_step_completed", { step: quest });
      const next = PERSONA_QUESTS.find((id) => id !== quest && !completeness.quests[id]);
      if (next) setActiveQuest(next);
    },
    [completeness.quests, setActiveQuest]
  );

  return {
    activeQuest,
    setActiveQuest,
    completeness,
    isSaving: updateProfile.isPending,
    tryOn,
    saveFields,
    completeQuest,
    skip,
    leave,
    shouldShowOnboarding,
  };
};
